import type { PropsWithChildren } from 'react';
import { ButtonNewScreener } from './ButtonNewScreener';
import DataTable from '../../DataTable';
import { Truncate, HiddenTr, FakeHeader } from '../styled';

/**
 * Common table components for MyScreeners
 */
export const Table = ({
  children,
  isBusy,
}: PropsWithChildren<{ isBusy?: boolean }>) => (
  <DataTable
    columnWidths="7, 3"
    columnTextAlignments="l,r"
    data-cy-id="my-screeners-table"
    style={{
      tableLayout: 'fixed', // Fixed layout prevents table from resizing
      position: 'relative',
    }}
    aria-busy={isBusy ? true : undefined}
  >
    {children}
  </DataTable>
);

const Head = ({ children }: PropsWithChildren) => (
  <thead>
    <HiddenTr>
      <th>
        <Truncate>Saved Screeners</Truncate>
      </th>
      <th>
        <Truncate>New Companies Last 7d</Truncate>
      </th>
    </HiddenTr>
    {children && (
      <tr>
        <td colSpan={2}>{children}</td>
      </tr>
    )}
  </thead>
);

/**
 * With tables, when we give the first table column the most space possible the
 * table header label in the second column will truncate as it doesn't have
 * enough width to display the label.
 * To fix this we create a fake header over the top to show much more of the
 * second column header label before truncating.
 * This technique preserves the table data structure while hiding the fake
 * header from screen readers.
 * It's a little hacky but it's the best solution we have for now.
 */
const HeadFake = () => (
  <FakeHeader aria-hidden>
    <div style={{ whiteSpace: 'nowrap' }}>Saved Screeners</div>
    <Truncate>New Companies Last 7d</Truncate>
  </FakeHeader>
);

const Body = ({ children }: PropsWithChildren) => <tbody>{children}</tbody>;

const Foot = () => (
  <tfoot>
    <tr>
      <th colSpan={2} style={{ paddingBlock: '10px 2px' }}>
        <ButtonNewScreener />
      </th>
    </tr>
  </tfoot>
);

Table.Head = Head;
Table.HeadFake = HeadFake;
Table.Body = Body;
Table.Foot = Foot;
